import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { useBuildStatus, BuildStatus } from "../../../../hooks/useBuildStatus";
import { useLogoGenerator } from "../../../../hooks/useLogoGenerator";
import { LogoGeneratorModal } from "../project-dashboard/LogoGeneratorModal";
import { WebsiteGeneratorModal } from "../project-dashboard/WebsiteGeneratorModal";
import { SEOAgentModal } from "../project-dashboard/SEOAgentModal";
import { ContentCreatorModal } from "../project-dashboard/ContentCreatorModal";
import { EmailSequenceModal } from "../project-dashboard/EmailSequenceModal";
import { SocialAssetsModal } from "../project-dashboard/SocialAssetsModal";
import { MarketingPlanModal } from "../project-dashboard/MarketingPlanModal";
import { SurveyBuilderModal } from "../project-dashboard/SurveyBuilderModal";
import { MVPScaffolderModal } from "../project-dashboard/MVPScaffolderModal";

interface Props {
  userID: string;
  projectTitle: string;
  projectID: string;
  projectDescription: string;
}

const CHECKLIST_ITEMS = [
  { id: "validate", text: "Complete all 24 validation steps", icon: "bi-check2-square" },
  { id: "scores", text: "Address any categories scoring below 40%", icon: "bi-bar-chart-line" },
  { id: "export", text: "Export your Build Package (click Build It)", icon: "bi-download" },
  { id: "pipeline", text: "Run the build pipeline on your machine", icon: "bi-terminal" },
  { id: "review", text: "Review your built MVP locally", icon: "bi-eye" },
  { id: "branding", text: "Generate your logo and branding assets", icon: "bi-palette" },
  { id: "domain", text: "Set up your domain and hosting", icon: "bi-globe" },
  { id: "email", text: "Create your email sequences", icon: "bi-envelope-at" },
  { id: "social", text: "Prepare your social media launch posts", icon: "bi-share" },
  { id: "launch", text: "Launch and share your product", icon: "bi-rocket-takeoff" },
];

interface BuildStatusConfig {
  bg: string;
  border: string;
  iconBg: string;
  icon: string;
  title: string;
  desc: string;
  titleColor: string;
  descColor: string;
}

const BUILD_STATUS_CONFIG: Record<BuildStatus, BuildStatusConfig> = {
  idle: {
    bg: "#F8FAFC",
    border: "#E2E8F0",
    iconBg: "#94A3B8",
    icon: "bi-hourglass",
    title: "Not Started",
    desc: "Export your build package to queue a build.",
    titleColor: "#475569",
    descColor: "#94A3B8",
  },
  queued: {
    bg: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
    border: "#3B82F6",
    iconBg: "#3B82F6",
    icon: "bi-gear-fill",
    title: "Build Queued",
    desc: "Run the build pipeline on your machine to start building.",
    titleColor: "#1E3A5F",
    descColor: "#2563EB",
  },
  building: {
    bg: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
    border: "#3B82F6",
    iconBg: "#3B82F6",
    icon: "bi-gear-fill",
    title: "Build in Progress",
    desc: "The build pipeline is running. This may take a few minutes.",
    titleColor: "#1E3A5F",
    descColor: "#2563EB",
  },
  complete: {
    bg: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
    border: "#8B5CF6",
    iconBg: "#8B5CF6",
    icon: "bi-rocket-takeoff-fill",
    title: "Build Complete!",
    desc: "Your MVP has been built by the pipeline.",
    titleColor: "#3B0764",
    descColor: "#6D28D9",
  },
  failed: {
    bg: "linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 100%)",
    border: "#EF4444",
    iconBg: "#EF4444",
    icon: "bi-exclamation-circle-fill",
    title: "Build Failed",
    desc: "The build pipeline encountered an error. Check the logs and try again.",
    titleColor: "#7F1D1D",
    descColor: "#B91C1C",
  },
};

export const LaunchDashboardPage: React.FC<Props> = ({
  userID,
  projectTitle,
  projectID,
  projectDescription,
}) => {
  const navigate = useNavigate();
  const { buildStatus, buildUrl, isPolling } = useBuildStatus(userID, projectID);
  const { generateLogo, isGenerating: isGeneratingLogo, error: logoError } = useLogoGenerator();

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [checklist, setChecklist] = useState<boolean[]>(
    new Array(CHECKLIST_ITEMS.length).fill(false)
  );

  // Modal visibility states
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showSEOModal, setShowSEOModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showMarketingModal, setShowMarketingModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showMVPModal, setShowMVPModal] = useState(false);

  // Load project data (logo URL)
  const fetchProjectData = useCallback(async () => {
    if (!userID || !projectID) return;
    try {
      const projectRef = doc(db, "users", userID, "projects", projectID);
      const snap = await getDoc(projectRef);
      const data = snap.data();
      if (data?.logoUrl) setLogoUrl(data.logoUrl as string);
    } catch {
      // Non-critical
    }
  }, [userID, projectID]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  const saveLogo = async (url: string) => {
    setLogoUrl(url);
    if (!userID || !projectID) return;
    try {
      const projectRef = doc(db, "users", userID, "projects", projectID);
      await updateDoc(projectRef, {
        logoUrl: url,
        logoUpdatedAt: new Date().toISOString(),
      });
    } catch {
      // Non-critical
    }
  };

  const toggleCheck = (index: number) => {
    setChecklist((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const completedCount = checklist.filter(Boolean).length;
  const sc = BUILD_STATUS_CONFIG[buildStatus];
  const isActiveBuild = buildStatus === "queued" || buildStatus === "building";

  const genTools = [
    { id: "logo", icon: "bi-image", label: "Logo", desc: "Generate your brand logo", color: "#EC4899", onOpen: () => setShowLogoModal(true) },
    { id: "website", icon: "bi-globe", label: "Landing Page", desc: "Build your launch page", color: "#3B82F6", onOpen: () => setShowWebsiteModal(true) },
    { id: "seo", icon: "bi-search", label: "SEO Agent", desc: "Keywords + blog content", color: "#10B981", onOpen: () => setShowSEOModal(true) },
    { id: "content", icon: "bi-file-text", label: "Content", desc: "Articles + copy", color: "#F59E0B", onOpen: () => setShowContentModal(true) },
    { id: "email", icon: "bi-envelope-at", label: "Email Sequences", desc: "Onboarding + nurture", color: "#3B82F6", onOpen: () => setShowEmailModal(true) },
    { id: "social", icon: "bi-share", label: "Social Assets", desc: "Twitter, LinkedIn, OG", color: "#8B5CF6", onOpen: () => setShowSocialModal(true) },
    { id: "marketing", icon: "bi-megaphone", label: "Marketing Plan", desc: "Channel + campaign plan", color: "#EF4444", onOpen: () => setShowMarketingModal(true) },
    { id: "survey", icon: "bi-clipboard-data", label: "Survey Builder", desc: "User research survey", color: "#10B981", onOpen: () => setShowSurveyModal(true) },
    { id: "mvp", icon: "bi-code-slash", label: "MVP Scaffolder", desc: "App code scaffold", color: "#475569", onOpen: () => setShowMVPModal(true) },
  ];

  return (
    <>
      {/* begin::Page header */}
      <div className="d-flex align-items-center mb-6" style={{ gap: "12px" }}>
        <button
          onClick={() => navigate("/project", { state: { projectTitle, projectID, projectDescription, userID } })}
          style={{
            background: "transparent",
            color: "#475569",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            padding: "6px 14px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            flexShrink: 0,
            transition: "all 150ms ease-out",
          }}
        >
          <i className="bi bi-arrow-left me-1" />
          Back to Project
        </button>
        <div>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#0F172A",
              letterSpacing: "-0.01em",
              marginBottom: "2px",
              lineHeight: 1.3,
            }}
          >
            <i className="bi bi-rocket-takeoff-fill me-2" style={{ color: "#8B5CF6", fontSize: "22px" }} />
            Launch Dashboard
          </h1>
          <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>{projectTitle}</p>
        </div>
      </div>
      {/* end::Page header */}

      {/* begin::Build Status Card */}
      <div
        className="card mb-5"
        style={{
          background: sc.bg,
          border: `1px solid ${sc.border}`,
          borderRadius: "12px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <div className="card-body py-4 px-6">
          <div className="d-flex align-items-center gap-4">
            <div
              style={{
                width: "48px",
                height: "48px",
                background: sc.iconBg,
                borderRadius: "9999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                animation: isActiveBuild ? "spin 1.5s linear infinite" : "none",
              }}
            >
              <i className={`bi ${sc.icon}`} style={{ color: "white", fontSize: "20px" }} />
            </div>
            <div className="flex-grow-1">
              <h4 style={{ fontSize: "16px", fontWeight: 600, color: sc.titleColor, marginBottom: "2px" }}>
                Build Status: {sc.title}
              </h4>
              <p style={{ fontSize: "13px", color: sc.descColor, marginBottom: 0 }}>
                {sc.desc}
                {isPolling && (
                  <span style={{ marginLeft: "6px", opacity: 0.7 }}>Checking for updates…</span>
                )}
              </p>
            </div>
            {buildStatus === "complete" && buildUrl && (
              <a
                href={buildUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#8B5CF6",
                  color: "white",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                  padding: "10px 20px",
                  textDecoration: "none",
                  flexShrink: 0,
                  transition: "all 150ms ease-out",
                }}
              >
                <i className="bi bi-box-arrow-up-right me-2" />
                View Project
              </a>
            )}
          </div>
        </div>
      </div>
      {/* end::Build Status Card */}

      {/* begin::Checklist + Tools row */}
      <div className="row g-5 mb-5">
        {/* Launch Checklist */}
        <div className="col-12 col-lg-5">
          <div
            className="card h-100"
            style={{
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <div className="card-body p-6">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#0F172A", margin: 0 }}>
                  <i className="bi bi-list-check me-2" style={{ color: "#3B82F6" }} />
                  Launch Checklist
                </h3>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: "JetBrains Mono, monospace",
                    color: completedCount === CHECKLIST_ITEMS.length ? "#10B981" : "#475569",
                  }}
                >
                  {completedCount}/{CHECKLIST_ITEMS.length}
                </span>
              </div>
              {/* Progress bar */}
              <div
                style={{
                  height: "6px",
                  background: "#F1F5F9",
                  borderRadius: "9999px",
                  marginBottom: "20px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%`,
                    background: completedCount === CHECKLIST_ITEMS.length ? "#10B981" : "#3B82F6",
                    borderRadius: "9999px",
                    transition: "width 400ms ease-out",
                  }}
                />
              </div>
              {/* Checklist items */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {CHECKLIST_ITEMS.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => toggleCheck(index)}
                    role="checkbox"
                    aria-checked={checklist[index]}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") toggleCheck(index);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      cursor: "pointer",
                      padding: "8px 10px",
                      borderRadius: "8px",
                      background: checklist[index] ? "#F0FDF4" : "transparent",
                      transition: "background 150ms ease-out",
                    }}
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "5px",
                        border: `2px solid ${checklist[index] ? "#10B981" : "#CBD5E1"}`,
                        background: checklist[index] ? "#10B981" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 150ms ease-out",
                      }}
                    >
                      {checklist[index] && (
                        <i className="bi bi-check" style={{ color: "white", fontSize: "12px", lineHeight: 1 }} />
                      )}
                    </div>
                    <i
                      className={`bi ${item.icon}`}
                      style={{
                        fontSize: "13px",
                        color: checklist[index] ? "#10B981" : "#94A3B8",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        color: checklist[index] ? "#94A3B8" : "#0F172A",
                        textDecoration: checklist[index] ? "line-through" : "none",
                        transition: "all 150ms ease-out",
                      }}
                    >
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
              {/* All done message */}
              {completedCount === CHECKLIST_ITEMS.length && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "10px 14px",
                    background: "#D1FAE5",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i className="bi bi-trophy-fill" style={{ color: "#10B981", fontSize: "16px" }} />
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#065F46" }}>
                    You're launched! 🎉
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Generation Tools */}
        <div className="col-12 col-lg-7">
          <div
            className="card h-100"
            style={{
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <div className="card-body p-6">
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#0F172A", marginBottom: "6px" }}>
                <i className="bi bi-magic me-2" style={{ color: "#8B5CF6" }} />
                Generation Tools
              </h3>
              <p style={{ fontSize: "13px", color: "#475569", marginBottom: "20px" }}>
                AI-powered tools to generate all your launch assets.
              </p>
              <div className="row g-3">
                {genTools.map((tool) => (
                  <div key={tool.id} className="col-md-4 col-sm-6">
                    <button
                      onClick={tool.onOpen}
                      style={{
                        width: "100%",
                        background: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "10px",
                        padding: "14px 12px",
                        textAlign: "left",
                        cursor: "pointer",
                        transition: "all 150ms ease-out",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0,0,0,0.1)";
                        e.currentTarget.style.borderColor = "#CBD5E1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.borderColor = "#E2E8F0";
                      }}
                    >
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          background: `${tool.color}18`,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <i className={`bi ${tool.icon}`} style={{ color: tool.color, fontSize: "15px" }} />
                      </div>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A", marginBottom: "2px" }}>
                        {tool.label}
                      </div>
                      <div style={{ fontSize: "11px", color: "#94A3B8", lineHeight: 1.4 }}>
                        {tool.desc}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* end::Checklist + Tools row */}

      {/* begin::Assets Overview */}
      <div
        className="card mb-5"
        style={{
          background: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <div className="card-body p-6">
          <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#0F172A", marginBottom: "20px" }}>
            <i className="bi bi-collection me-2" style={{ color: "#3B82F6" }} />
            Assets Overview
          </h3>
          <div className="row g-4">
            {/* Logo */}
            <div className="col-md-3 col-sm-6">
              <div
                style={{
                  background: "#F8FAFC",
                  borderRadius: "10px",
                  border: "1px solid #E2E8F0",
                  padding: "20px",
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {logoUrl ? (
                  <>
                    <img
                      src={logoUrl}
                      alt="Brand Logo"
                      style={{
                        width: "64px",
                        height: "64px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        border: "1px solid #E2E8F0",
                        background: "white",
                        padding: "4px",
                      }}
                    />
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>Logo</div>
                    <div style={{ fontSize: "11px", color: "#10B981", fontWeight: 500 }}>
                      <i className="bi bi-check-circle me-1" />
                      Generated
                    </div>
                    <button
                      onClick={() => setShowLogoModal(true)}
                      style={{
                        background: "transparent",
                        border: "1px solid #E2E8F0",
                        borderRadius: "6px",
                        padding: "4px 10px",
                        fontSize: "11px",
                        color: "#475569",
                        cursor: "pointer",
                        fontWeight: 500,
                        transition: "all 150ms ease-out",
                      }}
                    >
                      Regenerate
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        background: "#F1F5F9",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-image" style={{ fontSize: "20px", color: "#94A3B8" }} />
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>Logo</div>
                    <div style={{ fontSize: "11px", color: "#94A3B8" }}>Not yet generated</div>
                    <button
                      onClick={() => setShowLogoModal(true)}
                      style={{
                        background: "#3B82F6",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 14px",
                        fontSize: "11px",
                        color: "white",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 150ms ease-out",
                      }}
                    >
                      Generate
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Other asset cards */}
            {[
              {
                key: "email",
                label: "Email Sequences",
                icon: "bi-envelope-at",
                desc: "Onboarding, nurture & re-engagement emails",
                color: "#3B82F6",
                onOpen: () => setShowEmailModal(true),
              },
              {
                key: "social",
                label: "Social Assets",
                icon: "bi-share",
                desc: "Twitter, LinkedIn, Instagram & Facebook copy",
                color: "#8B5CF6",
                onOpen: () => setShowSocialModal(true),
              },
              {
                key: "seo",
                label: "SEO Content",
                icon: "bi-search",
                desc: "Keywords, blog posts & landing pages",
                color: "#10B981",
                onOpen: () => setShowSEOModal(true),
              },
            ].map((asset) => (
              <div key={asset.key} className="col-md-3 col-sm-6">
                <div
                  style={{
                    background: "#F8FAFC",
                    borderRadius: "10px",
                    border: "1px solid #E2E8F0",
                    padding: "20px",
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: `${asset.color}15`,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i className={`bi ${asset.icon}`} style={{ fontSize: "20px", color: asset.color }} />
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>{asset.label}</div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#94A3B8",
                      lineHeight: 1.4,
                      maxWidth: "130px",
                    }}
                  >
                    {asset.desc}
                  </div>
                  <button
                    onClick={asset.onOpen}
                    style={{
                      background: asset.color,
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 14px",
                      fontSize: "11px",
                      color: "white",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 150ms ease-out",
                    }}
                  >
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* end::Assets Overview */}

      {/* begin::Modals */}
      <LogoGeneratorModal
        show={showLogoModal}
        handleClose={() => setShowLogoModal(false)}
        projectTitle={projectTitle}
        projectDescription={projectDescription}
        generateLogo={(prompt, style) => generateLogo(prompt, style, projectTitle)}
        isGenerating={isGeneratingLogo}
        error={logoError}
        onLogoGenerated={saveLogo}
      />
      <WebsiteGeneratorModal
        show={showWebsiteModal}
        handleClose={() => setShowWebsiteModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
        projectDescription={projectDescription}
      />
      <SEOAgentModal
        show={showSEOModal}
        handleClose={() => setShowSEOModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <ContentCreatorModal
        show={showContentModal}
        handleClose={() => setShowContentModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <EmailSequenceModal
        show={showEmailModal}
        handleClose={() => setShowEmailModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <SocialAssetsModal
        show={showSocialModal}
        handleClose={() => setShowSocialModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <MarketingPlanModal
        show={showMarketingModal}
        handleClose={() => setShowMarketingModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <SurveyBuilderModal
        show={showSurveyModal}
        handleClose={() => setShowSurveyModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <MVPScaffolderModal
        show={showMVPModal}
        handleClose={() => setShowMVPModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      {/* end::Modals */}
    </>
  );
};
